/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {FC, memo} from 'react';
import {Box, makeStyles, Typography} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {get} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {CancelButton} from '@ion/dls/src/editors/controls/CancelButton';
import styles, {getClasses} from '@ion/dls/src/editors/editorStyles';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {SocialFaceBookIcon} from '@ion/dls/src/icons/SocialFaceBookIcon';
import {SocialLinkedInIcon} from '@ion/dls/src/icons/SocialLinkedInIcon';
import {SocialTwitterIcon} from '@ion/dls/src/icons/SocialTwitterIcon';
import {SocialYouTubeIcon} from '@ion/dls/src/icons/SocialYouTubeIcon';
import {SystemCallOutlineIcon} from '@ion/dls/src/icons/SystemCallOutlineIcon';
import {SystemEmailIcon} from '@ion/dls/src/icons/SystemEmailIcon';
import Render from '@ion/dls/src/render/Render';
import {getSupportDetails} from '@ion/dls/src/theme/resellers/Resellers';
import {dlsAlt, dlsColor, dlsLayout} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import {openFacebook, openLinkedIn, openTwitter, openYoutube} from './ContactSupportUtils';

export interface ContactSupportProps {
  onCancel?: () => void;
};

const useStyles = makeStyles(theme => ({
  support : {
    margin          : '60px 0',
    padding         : '28px 40px',
    height          : 400,
    backgroundColor : dlsColor('darkTeal'),
    display         : 'flex',
    flexDirection   : 'column',
    '& a'           : {
      textDecoration : 'none',
      color          : 'inherit'
    },
    [theme.breakpoints.up('sm')] : {
      width : 350
    },
    [theme.breakpoints.down('sm')] : {
      padding : '28px 10px'
    }
  },
  contactUs : {
    color        : dlsColor('white'),
    marginBottom : 40
  },
  contactUsText : {
    ...dlsAlt('typography', 'cta2'),
    color      : dlsColor('white'),
    marginLeft : dlsLayout('spacing') * 2
  },
  empty : {
    flex : 1
  },
  socialIcon : {
    margin : `0 ${dlsLayout('spacing')*2}px`,
    cursor : 'pointer'
  },
  email : {
    marginBottom : dlsLayout('spacing') * 4
  },
  button : {
    borderTop : `solid 1px ${dlsColor('grey4')}`
  }
}));

/**
 * The Contact Support modal with CalAmp Social and contact details.
 */
export const ContactSupport: FC<ContactSupportProps> = ({onCancel}) => {
  const classes       = useStyles();
  const editorStyles  =  useExtendStyles(styles);
  const dialogStyle   =  editorStyles();

  const {
    email,
    phone,
    facebook,
    linkedIn,
    twitter,
    youtube
  } = getSupportDetails();

  return (
    <Dialog open={true}
      classes={getClasses(dialogStyle)}>
      <DialogTitle classes={{root: dialogStyle.formDialogTitle}}>
        <div className={dialogStyle.formTitle}>
          <div onClick={onCancel} className={dialogStyle.closeButton}>
            <MaterialExitCloseIcon size={IconSize.medium} color={dlsColor('grey3')} />
          </div>
          <Typography component='h1' variant='h2'>
            {translate('nav:ionprime.contactSupport')}
          </Typography>
        </div>
      </DialogTitle>
      <DialogContent className={dialogStyle.formDialogContent}>
        <div className={classes.support}>
          <div>
            <HeaderTypography className={classes.contactUs}>
              {translate('common:support.contactUs')}
            </HeaderTypography>
            <Render.If condition={email}>
              <a href={`mailto:${email}`}>
                <Box display="flex"
                  alignItems="center"
                  className={classes.email}>
                  <SystemEmailIcon size={IconSize.medium} color={dlsColor('white')} />
                  <div className={classes.contactUsText}>{email}</div>
                </Box>
              </a>
            </Render.If>
            <Render.If condition={phone}>
              <a href={`tel:${phone}`}>
                <Box display="flex"
                  alignItems="center">
                  <SystemCallOutlineIcon size={IconSize.medium} color={dlsColor('white')} />
                  <div className={classes.contactUsText}>{phone}</div>
                </Box>
              </a>
            </Render.If>
          </div>
          <div className={classes.empty} />
          <Box display="flex" alignItems="center">
            <Render.If condition={linkedIn}>
              <div className={classes.socialIcon}
                onClick={openLinkedIn}>
                <SocialLinkedInIcon size={IconSize.medium} color={dlsColor('white')} />
              </div>
            </Render.If>
            <Render.If condition={facebook}>
              <div className={classes.socialIcon}
                onClick={openFacebook}>
                <SocialFaceBookIcon size={IconSize.medium} color={dlsColor('white')} />
              </div>
            </Render.If>
            <Render.If condition={twitter}>
              <div className={classes.socialIcon}
                onClick={openTwitter}>
                <SocialTwitterIcon size={IconSize.medium} color={dlsColor('white')} />
              </div>
            </Render.If>
            <Render.If condition={youtube}>
              <div className={classes.socialIcon}
                onClick={openYoutube}>
                <SocialYouTubeIcon size={IconSize.medium} color={dlsColor('white')} />
              </div>
            </Render.If>
          </Box>
        </div>
      </DialogContent>
      <DialogActions className={classes.button}>
        <CancelButton onCancel={onCancel} />
      </DialogActions>
    </Dialog>
  );
};

export default memo(ContactSupport);
